import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Skeleton } from "@chakra-ui/react";
import { useFlag, useFlagsStatus, useVariant } from "@unleash/proxy-client-react";

import { useAppModal } from "../../../Modals";
import { RoutesMap } from "../../../../../routes";
import { UnleashFlags } from "../../../../../constants";
import { PaymentBlockModal } from "../PaymentBlockModal";
import { useGetCardsQuery } from "../../../../../api/cards";
import { useGetDebtStateQuery } from "../../../../../api/debt";
import { useCheckActivePaymentQuery } from "../../../../../api/payments";
import { getMainPaymentMethod, isStringWithNumber } from "../../../../../utils";
import { PaymentDestinationType } from "../../../../../api/payments/types.ts";

interface Props {
  currentAmount: string;
  nearestPaymentAmount: string | undefined;
  paymentDestination: PaymentDestinationType;
  isNearestToday: boolean;
  clearInput: () => void;
  resetPaymentDestination: () => void;
}

export const PaymentBlockPaymentButton: FC<Props> = (props) => {
  const {
    currentAmount,
    nearestPaymentAmount,
    paymentDestination,
    isNearestToday,
    clearInput,
    resetPaymentDestination,
  } = props;

  const navigate = useNavigate();
  const { showModal, hideModal } = useAppModal();

  const flagsStatus = useFlagsStatus();
  const flagsReady = flagsStatus.flagsReady;
  const flagsError = Boolean(flagsStatus.flagsError);

  const { data: debt, isPending: isDebtPending } = useGetDebtStateQuery();
  const { data: cards, isPending: isCardsPending } = useGetCardsQuery();
  const {
    data: lastPaymentStatus,
    isFetching: isLastPaymentStatusFetching,
  } = useCheckActivePaymentQuery();

  const isDebtRepaymentOptionsEnabled = useFlag(UnleashFlags.debtRepaymentOptionsEnabled);
  const commissionVariant = useVariant(UnleashFlags.dfPaymentsCommission);
  const commissionValue = commissionVariant.payload?.value;

  const mainPaymentMethod = useMemo(() => {
    return getMainPaymentMethod(cards);
  }, [cards]);

  const fullDebtAmount = isStringWithNumber(debt?.total) ? Number(debt?.total) : 0;
  const hasOverdue = isStringWithNumber(debt?.overdue?.total) && Number(debt?.overdue?.total) > 0;
  const overdueAmount = Number(debt?.overdue?.total);

  const isPayButtonDisabled
      = !currentAmount
      || Number(currentAmount) > fullDebtAmount
      || lastPaymentStatus?.isPaymentActive === true
      || (
        mainPaymentMethod?.terminalType === "PAID"
        && (!flagsReady || flagsError || !isStringWithNumber(commissionValue))
      )
      || (!isNearestToday && !hasOverdue && isDebtRepaymentOptionsEnabled && !paymentDestination)
      || (
        isNearestToday
        && !hasOverdue
        && isStringWithNumber(nearestPaymentAmount)
        && (Number(currentAmount) > Number(nearestPaymentAmount))
        && isDebtRepaymentOptionsEnabled
        && !paymentDestination
      )
      || (
        hasOverdue
        && !isNearestToday
        && (Number(currentAmount) > overdueAmount)
        && isDebtRepaymentOptionsEnabled
        && !paymentDestination
      )
      || (
        isNearestToday
        && hasOverdue
        && isStringWithNumber(nearestPaymentAmount)
        && (Number(currentAmount) > (overdueAmount + Number(nearestPaymentAmount)))
        && isDebtRepaymentOptionsEnabled
        && !paymentDestination
      );

  const onPay = () => {
    if (currentAmount && mainPaymentMethod?.productId) {
      const modalHeader = isDebtRepaymentOptionsEnabled && paymentDestination === "EARLY_REPAYMENT"
        ? "Досрочное погашение"
        : "Платеж";

      showModal({
        header: modalHeader,
        content: (onClose, block) => (
          <PaymentBlockModal
            amount={currentAmount}
            paymentMethod={mainPaymentMethod}
            clearInput={clearInput}
            onCancel={onClose}
            hideModal={hideModal}
            blockModalClosing={block}
            paymentDestination={paymentDestination}
            resetPaymentDestination={resetPaymentDestination}
          />
        ),
      });
    }
  };

  const onAddPaymentMethod = () => {
    navigate(RoutesMap.cards.index);
  };

  if (isCardsPending || isDebtPending || isLastPaymentStatusFetching) {
    return <Skeleton h="52px" mt="24px" />;
  }

  if (!mainPaymentMethod) {
    return (
      <Button colorScheme="brand" mt="24px" onClick={onAddPaymentMethod}>
        Добавить способ оплаты
      </Button>
    );
  }

  return (
    <Button colorScheme="brand" mt="24px" onClick={onPay} isDisabled={isPayButtonDisabled}>
      Оплатить
    </Button>
  );
};
